import { useState, useCallback, useRef, useEffect } from "react";
import { toast } from "sonner";
import { useWebSocketContext } from "../../contexts/WebSocketContext";
import { logger } from "../../lib/logger";
import { useNpcPlayer } from "../../lib/useNpcPlayer";
import {
  ClientActionEnum,
  GameStatusEnum,
  PlayerActionStatusEnum,
  type GameState,
} from "../../types/game";
import { DEFAULT_NPC_CONFIG, type NpcConfig } from "../../types/npc";

interface UseGameBoardProps {
  sessionId: string;
  playerId: string;
  gameState: GameState | null;
  updateGameState: (state: GameState) => void;
  setSessionError: (error: string) => void;
}

export function useGameBoard({
  sessionId: _sessionId,
  playerId,
  gameState,
  updateGameState: _updateGameState,
  setSessionError: _setSessionError,
}: UseGameBoardProps) {
  // WebSocket is now managed by context
  const { sendMessage, status: wsStatus } = useWebSocketContext();

  const [selectedCardIndices, setSelectedCardIndices] = useState<number[]>([]);
  const [selectedAnswerIndex, setSelectedAnswerIndex] = useState<number | null>(
    null,
  );
  const [submitting, setSubmitting] = useState(false);
  const confettiFiredRef = useRef(false);

  // NPC player state
  const [npcConfig, setNpcConfig] = useState<NpcConfig>(DEFAULT_NPC_CONFIG);
  const showNpcControl =
    import.meta.env.DEV || window.location.search.includes("npc=true");
  const [showMagicCode, setShowMagicCode] = useState(true);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  // Reset selection state when game state changes (server confirmed action)
  const prevGameStateRef = useRef(gameState);
  useEffect(() => {
    if (gameState !== prevGameStateRef.current) {
      logger.debug("gameboard_state_update", { phase: gameState?.room_status });
      setSelectedCardIndices([]);
      setSelectedAnswerIndex(null);
      prevGameStateRef.current = gameState;
    }
  }, [gameState]);

  // NPC player hook
  const { actionLog: npcActionLog, clearLog: clearNpcLog } = useNpcPlayer({
    config: npcConfig,
    sendMessage,
  });

  // Event handlers
  const handleSubmitCards = useCallback(async () => {
    if (selectedCardIndices.length === 0) {
      toast.error("Please select at least one card");
      return;
    }

    setSubmitting(true);
    try {
      sendMessage(ClientActionEnum.PLAYER_SUBMIT_CARDS, {
        card_indices: selectedCardIndices,
      });
      setSelectedCardIndices([]);
    } catch (error) {
      logger.error("card_submission_failed", { error, selectedCardIndices });
    } finally {
      setSubmitting(false);
    }
  }, [selectedCardIndices, sendMessage]);

  const handleSelectAnswer = useCallback(
    async (index: number) => {
      if (!gameState) return;

      setSubmitting(true);
      try {
        const answerOptions = gameState.judge_answer_options || [];
        if (index >= answerOptions.length) {
          logger.error("invalid_answer_index", {
            index,
            optionsCount: answerOptions.length,
          });
          return;
        }
        const selectedCard = answerOptions[index];
        sendMessage(ClientActionEnum.JUDGE_SUBMIT_ANSWER, {
          card_id: selectedCard.card_id,
        });
        setSelectedAnswerIndex(index);
      } catch (error) {
        logger.error("answer_selection_failed", { error, index });
      } finally {
        setSubmitting(false);
      }
    },
    [gameState, sendMessage],
  );

  const handleSelectWinner = useCallback(
    async (winnerId: string) => {
      setSubmitting(true);
      try {
        sendMessage(ClientActionEnum.JUDGE_PICK_WINNER, {
          winner_player_id: winnerId,
        });
      } catch (error) {
        logger.error("winner_selection_failed", { error, winnerId });
      } finally {
        setSubmitting(false);
      }
    },
    [sendMessage],
  );

  const toggleCardSelection = useCallback((index: number) => {
    setSelectedCardIndices((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index);
      } else {
        return [...prev, index];
      }
    });
  }, []);

  const getActionPrompt = useCallback((): string => {
    if (!gameState) return "Loading...";

    const isJudge = gameState.is_judge;
    const currentStatus = gameState.room_status;
    const myActionStatus =
      gameState.player_action_statuses?.[playerId] || "idle";

    if (isJudge) {
      switch (currentStatus) {
        case GameStatusEnum.JUDGE_ADDS_ANSWER_TO_PROMPT:
          return "Select an answer card";
        case GameStatusEnum.JUDGE_PICKS_WINNER:
          return "Select the best (worst?) treatment!";
        default:
          return "Waiting...";
      }
    } else {
      switch (currentStatus) {
        case GameStatusEnum.PLAYERS_SUBMIT_CARDS:
          if (myActionStatus === PlayerActionStatusEnum.ACTION_TAKEN) {
            return "Waiting for other players...";
          }
          return "Select cards to submit";
        case GameStatusEnum.JUDGE_PICKS_WINNER:
          return "Psychologist is judging...";
        default:
          return "Waiting for psychologist...";
      }
    }
  }, [gameState, playerId]);

  return {
    // State
    selectedCardIndices,
    selectedAnswerIndex,
    submitting,
    confettiFiredRef,
    npcConfig,
    showNpcControl,
    showMagicCode,
    showLeaveConfirm,
    wsStatus,
    npcActionLog,
    // Actions
    setNpcConfig,
    setShowMagicCode,
    setShowLeaveConfirm,
    handleSubmitCards,
    handleSelectAnswer,
    handleSelectWinner,
    toggleCardSelection,
    getActionPrompt,
    sendMessage,
    clearNpcLog,
  };
}
